import { makeAutoObservable, runInAction } from 'mobx';
import { DashboardData, DatasetKey, DashboardRecord, DashboardVersion } from './types';
import { computeDashboard, generateForecast } from '../mock/computeDashboard';
import {
  DEFAULT_PLANNING_PARAMS,
  MOCK_PRODUCTS,
  MOCK_ROOMS,
  MOCK_CALENDAR,
  MOCK_BPM_DATA,
  MOCK_CHANGEOVER_SUMMARY,
  MOCK_JOB_HISTORY,
  MOCK_REFERENCE_DATA,
  MOCK_PLANNING_PARAMS_RAW,
  MOCK_INDIRECT_SUMMARY,
  buildCalendar,
} from '../mock/mockData';

// ---------------------------------------------------------------------------
// Delay helper — simulates async network latency in the demo
// ---------------------------------------------------------------------------
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Explorer dataset registry
// ---------------------------------------------------------------------------

type ExplorerRow = Record<string, unknown>;

/** Returns a shallow copy of the appropriate mock dataset for the explorer view. */
function getExplorerDataset(dataset: DatasetKey): ExplorerRow[] {
  switch (dataset) {
    case 'products':
      return MOCK_PRODUCTS as unknown as ExplorerRow[];
    case 'forecast':
      // Materialise the forecast on demand — keeps cold-start fast.
      return generateForecast() as unknown as ExplorerRow[];
    case 'rooms':
      return MOCK_ROOMS as unknown as ExplorerRow[];
    case 'bpm':
      return MOCK_BPM_DATA as unknown as ExplorerRow[];
    case 'changeovers':
      return MOCK_CHANGEOVER_SUMMARY as unknown as ExplorerRow[];
    case 'jobs':
      return MOCK_JOB_HISTORY as unknown as ExplorerRow[];
    case 'calendar':
      return MOCK_CALENDAR as unknown as ExplorerRow[];
    case 'reference':
      // Flatten reference sub-tables into a single list for the grid.
      return [
        ...MOCK_REFERENCE_DATA.run_types.map((r) => ({ table: 'run_types', ...r })),
        ...MOCK_REFERENCE_DATA.clean_types.map((r) => ({ table: 'clean_types', ...r })),
        ...MOCK_REFERENCE_DATA.coating_types.map((r) => ({ table: 'coating_types', ...r })),
        ...MOCK_PLANNING_PARAMS_RAW.map((r) => ({ table: 'planning_params', ...r })),
        ...MOCK_INDIRECT_SUMMARY.map((r) => ({ table: 'indirect_summary', ...r })),
      ] as unknown as ExplorerRow[];
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Dashboard version factory helper
// ---------------------------------------------------------------------------

function mkVer(
  versionId: string, label: string, description: string, createdAt: string,
  overrides: Record<string, number>, startYearMonth: string,
): DashboardVersion {
  return { versionId, label, description, createdAt, params: { ...DEFAULT_PLANNING_PARAMS, ...overrides }, startYearMonth };
}

/** The single Global Dashboard ID — exported so components can reference it. */
export const GLOBAL_DASHBOARD_ID = 'dash-global';

const INITIAL_DASHBOARD_RECORDS: DashboardRecord[] = [
  {
    id: GLOBAL_DASHBOARD_ID,
    name: 'GSMS Capacity Plan',
    description: 'Official capacity planning model for GSMS manufacturing operations. Covers all packaging lines, changeover profiles, and headcount requirements.',
    isPublished: true, ownedBy: 'Global',
    createdAt: '2025-10-01', updatedAt: '2026-04-10',
    activeVersionId: 'v3.0',
    versions: [
      mkVer('v1.0', 'v1.0 — FY2026 Initial',
        'Initial baseline — Q1 FY2026. Established baseline downtime (15%) and 2-shift schedule.',
        '2026-01-15', { downtime_factor: 0.15 }, '2026-04'),
      mkVer('v2.0', 'v2.0 — Q2 Revision',
        'Q2 revision: moved to 3-shift schedule to handle increased forecast volume. Downtime revised to 18%.',
        '2026-03-01', { shifts_per_day: 3, downtime_factor: 0.18 }, '2026-06'),
      mkVer('v2.1', 'v2.1 — Q2 Approved',
        'Q2 approved: downtime buffer raised to 22%, PA lead headcount increased to 3.',
        '2026-03-28', { shifts_per_day: 3, downtime_factor: 0.22, pa_leads: 3 }, '2026-06'),
      mkVer('v3.0', 'v3.0 — H2 Current',
        'Current approved plan: full 3-shift H2 2026 schedule with standard 20% downtime and lookback extended to 6 months.',
        '2026-04-10', { shifts_per_day: 3, downtime_factor: 0.20, pa_leads: 3, lookback_months: 6 }, '2026-10'),
    ],
  },
];

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export class CapacityPlannerStore {
  // -- Dashboard slice
  dashboardData:    DashboardData | null = null;
  isDashboardLoading = false;
  dashboardError:   string | null       = null;

  // -- Explorer slice
  explorerData:       ExplorerRow[] = [];
  isExplorerLoading   = false;
  selectedDataset:    DatasetKey    = 'products';

  // -- Planning params — initialized from global dashboard v3.0 defaults
  planningParams: Record<string, number> = {
    ...DEFAULT_PLANNING_PARAMS,
    shifts_per_day: 3, downtime_factor: 0.20, pa_leads: 3, lookback_months: 6,
  };

  // -- Start month for the 12-month planning window (default matches active version v3.0)
  startYearMonth: string = '2026-10';

  // -- View range filter (UI only — narrows displayed months without recalculation)
  viewFilterStart: string = '';   // '' means no filter applied
  viewFilterEnd:   string = '';   // '' means no filter applied

  // -- Active view  (default: open global dashboard immediately)
  activeView: 'listing' | 'dashboard' | 'explorer' | 'reference' | 'calculation' = 'dashboard';

  // -- Dashboard workspace management
  // Default: global dashboard at its latest active version
  activeDashboardId:        string | null   = GLOBAL_DASHBOARD_ID;
  activeDashboardVersionId: string          = 'v3.0';
  dashboardRecords:         DashboardRecord[] = INITIAL_DASHBOARD_RECORDS.map(d => ({ ...d }));

  // -- Long-running job state
  isJobRunning   = false;
  jobProgress    = 0;
  jobStatusText  = '';
  jobLog:        string[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  // ---------------------------------------------------------------------------
  // fetchDashboard
  // ---------------------------------------------------------------------------
  async fetchDashboard(): Promise<void> {
    runInAction(() => {
      this.isDashboardLoading = true;
      this.dashboardError     = null;
    });

    try {
      await delay(800);

      const cal      = buildCalendar(this.startYearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...this.planningParams }, this.startYearMonth);

      runInAction(() => {
        this.dashboardData     = data;
        this.isDashboardLoading = false;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data.';
      runInAction(() => {
        this.dashboardError     = message;
        this.isDashboardLoading = false;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // updatePlanningParamsAndRefresh
  // ---------------------------------------------------------------------------
  async updatePlanningParamsAndRefresh(params: Record<string, number>): Promise<void> {
    runInAction(() => {
      this.planningParams     = { ...params };
      this.isDashboardLoading = true;
      this.dashboardError     = null;
    });

    try {
      await delay(600);

      const cal      = buildCalendar(this.startYearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...this.planningParams }, this.startYearMonth);

      runInAction(() => {
        this.dashboardData     = data;
        this.isDashboardLoading = false;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to refresh dashboard.';
      runInAction(() => {
        this.dashboardError     = message;
        this.isDashboardLoading = false;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // updateStartMonthAndRefresh
  // ---------------------------------------------------------------------------
  async updateStartMonthAndRefresh(yearMonth: string): Promise<void> {
    runInAction(() => {
      this.startYearMonth     = yearMonth;
      this.isDashboardLoading = true;
      this.dashboardError     = null;
    });
    try {
      await delay(600);
      const cal      = buildCalendar(yearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...this.planningParams }, yearMonth);
      runInAction(() => {
        this.dashboardData      = data;
        this.isDashboardLoading = false;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to refresh.';
      runInAction(() => {
        this.dashboardError     = message;
        this.isDashboardLoading = false;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // fetchExplorerData
  // ---------------------------------------------------------------------------
  async fetchExplorerData(dataset: DatasetKey): Promise<void> {
    runInAction(() => {
      this.selectedDataset    = dataset;
      this.isExplorerLoading  = true;
    });

    try {
      await delay(400);

      const rows = getExplorerDataset(dataset);

      runInAction(() => {
        this.explorerData      = rows;
        this.isExplorerLoading = false;
      });
    } catch (_err: unknown) {
      // Explorer errors are non-critical — clear spinner and leave data stale.
      runInAction(() => {
        this.isExplorerLoading = false;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // resetStore
  // ---------------------------------------------------------------------------
  resetStore(): void {
    runInAction(() => {
      this.dashboardData      = null;
      this.isDashboardLoading = false;
      this.dashboardError     = null;

      this.explorerData       = [];
      this.isExplorerLoading  = false;
      this.selectedDataset    = 'products';

      this.planningParams  = { ...DEFAULT_PLANNING_PARAMS };
      this.startYearMonth  = '2026-04';
      this.activeView      = 'dashboard';
    });
  }

  // ---------------------------------------------------------------------------
  // applyPlanningSettings — combined start-month + params update (7-second job)
  // ---------------------------------------------------------------------------
  async applyPlanningSettings(yearMonth: string, params: Record<string, number>): Promise<void> {
    runInAction(() => {
      this.startYearMonth = yearMonth;
      this.planningParams = { ...params };
      this.dashboardError = null;
    });

    await this.simulateJob();  // 7 seconds with progress overlay

    try {
      const cal      = buildCalendar(yearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...params }, yearMonth);
      runInAction(() => {
        this.dashboardData     = data;
        this.isJobRunning      = false;
        this.jobProgress       = 100;
        this.jobStatusText     = 'Complete';
        this.isDashboardLoading = false;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to apply settings.';
      runInAction(() => {
        this.dashboardError   = message;
        this.isJobRunning     = false;
        this.isDashboardLoading = false;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // simulateJob — 7-second fake computation with step progress
  // ---------------------------------------------------------------------------
  private async simulateJob(): Promise<void> {
    const steps: { at: number; progress: number; text: string }[] = [
      { at: 0,    progress: 5,   text: 'Initializing job...' },
      { at: 900,  progress: 18,  text: 'Fetching forecast data from PLM...' },
      { at: 1800, progress: 34,  text: 'Processing changeover profiles...' },
      { at: 3000, progress: 52,  text: 'Computing headcount requirements...' },
      { at: 4200, progress: 68,  text: 'Aggregating room utilization...' },
      { at: 5400, progress: 82,  text: 'Building dashboard summary...' },
      { at: 6300, progress: 94,  text: 'Finalizing output...' },
    ];

    runInAction(() => {
      this.isJobRunning  = true;
      this.jobProgress   = 0;
      this.jobStatusText = 'Starting...';
      this.jobLog        = [];
    });

    steps.forEach(({ at, progress, text }) => {
      setTimeout(() => {
        runInAction(() => {
          this.jobProgress   = progress;
          this.jobStatusText = text;
          this.jobLog        = [...this.jobLog, text];
        });
      }, at);
    });

    return new Promise((resolve) => setTimeout(resolve, 7000));
  }

  // ---------------------------------------------------------------------------
  // openDashboard — loads a dashboard record (specific version or active)
  // ---------------------------------------------------------------------------
  async openDashboard(id: string, versionId?: string): Promise<void> {
    const record = this.dashboardRecords.find((d) => d.id === id);
    if (!record) return;
    const vid  = versionId ?? record.activeVersionId;
    const ver  = record.versions.find((v) => v.versionId === vid);
    if (!ver) return;

    runInAction(() => {
      this.activeDashboardId        = id;
      this.activeDashboardVersionId = vid;
      this.startYearMonth           = ver.startYearMonth;
      this.planningParams           = { ...ver.params };
      this.isDashboardLoading       = true;
      this.dashboardError           = null;
    });

    try {
      await delay(800);
      const cal      = buildCalendar(ver.startYearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...ver.params }, ver.startYearMonth);
      runInAction(() => {
        this.dashboardData      = data;
        this.isDashboardLoading = false;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard.';
      runInAction(() => { this.dashboardError = message; this.isDashboardLoading = false; });
    }
  }

  // ---------------------------------------------------------------------------
  // switchVersion — fast version switch (no 7s job)
  // ---------------------------------------------------------------------------
  async switchVersion(dashboardId: string, versionId: string): Promise<void> {
    const record = this.dashboardRecords.find((d) => d.id === dashboardId);
    if (!record) return;
    const ver = record.versions.find((v) => v.versionId === versionId);
    if (!ver) return;

    runInAction(() => {
      this.activeDashboardVersionId = versionId;
      this.startYearMonth           = ver.startYearMonth;
      this.planningParams           = { ...ver.params };
      this.isDashboardLoading       = true;
    });

    try {
      await delay(600);
      const cal      = buildCalendar(ver.startYearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...ver.params }, ver.startYearMonth);
      runInAction(() => { this.dashboardData = data; this.isDashboardLoading = false; });
    } catch (err: unknown) {
      runInAction(() => { this.isDashboardLoading = false; });
    }
  }

  // ---------------------------------------------------------------------------
  // backToLanding — returns to the workspace listing screen
  // ---------------------------------------------------------------------------
  backToLanding(): void {
    runInAction(() => {
      this.activeDashboardId        = null;
      this.activeDashboardVersionId = '';
      this.dashboardData            = null;
      this.activeView               = 'listing';
    });
  }

  // ---------------------------------------------------------------------------
  // createPrivateDashboard
  // ---------------------------------------------------------------------------
  createPrivateDashboard(name: string): void {
    const id = `local-${Date.now()}`;
    const ver: DashboardVersion = {
      versionId:      'draft',
      label:          'draft',
      description:    'Working draft',
      createdAt:      new Date().toISOString().slice(0, 10),
      params:         { ...DEFAULT_PLANNING_PARAMS },
      startYearMonth: this.startYearMonth,
    };
    const record: DashboardRecord = {
      id, name: name.trim() || 'New Dashboard',
      description:    'Private draft dashboard',
      isPublished:    false,
      ownedBy:        'You',
      createdAt:      new Date().toISOString().slice(0, 10),
      updatedAt:      new Date().toISOString().slice(0, 10),
      versions:       [ver],
      activeVersionId: 'draft',
    };
    runInAction(() => {
      this.dashboardRecords         = [...this.dashboardRecords, record];
      // Open directly — no fetch, no pre-generated data
      this.activeDashboardId        = id;
      this.activeDashboardVersionId = 'draft';
      this.startYearMonth           = '2026-04';
      this.planningParams           = { ...DEFAULT_PLANNING_PARAMS };
      this.dashboardData            = null;
      this.isDashboardLoading       = false;
      this.dashboardError           = null;
    });
  }

  // ---------------------------------------------------------------------------
  // shareLocalDashboard — marks private dashboard as shared (visible to team)
  // Does NOT affect the global dashboard.
  // ---------------------------------------------------------------------------
  shareLocalDashboard(id: string): void {
    runInAction(() => {
      const today = new Date().toISOString().slice(0, 10);
      this.dashboardRecords = this.dashboardRecords.map((d) => {
        if (d.id !== id) return d;
        const versions = d.versions.map((v) =>
          v.versionId === 'draft'
            ? { ...v, versionId: 'v1.0', label: 'v1.0', description: 'Initial shared version' }
            : v,
        );
        const activeVersionId = d.activeVersionId === 'draft' ? 'v1.0' : d.activeVersionId;
        return { ...d, isPublished: true, updatedAt: today, versions, activeVersionId };
      });
      if (this.activeDashboardVersionId === 'draft') {
        this.activeDashboardVersionId = 'v1.0';
      }
    });
  }

  // ---------------------------------------------------------------------------
  // promoteLocalToGlobal — promotes a local dashboard as a new version of the
  // global dashboard, making it the new active global version.
  // ---------------------------------------------------------------------------
  async promoteLocalToGlobal(localId: string): Promise<void> {
    const local     = this.dashboardRecords.find((d) => d.id === localId);
    const globalRec = this.dashboardRecords.find((d) => d.id === GLOBAL_DASHBOARD_ID);
    if (!local || !globalRec) return;

    const localActiveVer = local.versions.find((v) => v.versionId === local.activeVersionId);
    if (!localActiveVer) return;

    // Calculate next global version number (always bumps major, e.g. v3.0 → v4.0)
    const maxMajor = Math.max(
      ...globalRec.versions.map((v) => {
        const m = v.versionId.match(/^v(\d+)/);
        return m ? parseInt(m[1], 10) : 0;
      }),
    );
    const newVersionId = `v${maxMajor + 1}.0`;
    const today = new Date().toISOString().slice(0, 10);

    const newGlobalVersion: DashboardVersion = {
      versionId:      newVersionId,
      label:          `${newVersionId} — ${local.name}`,
      description:    `Promoted from "${local.name}". ${localActiveVer.description}`,
      createdAt:      today,
      params:         { ...localActiveVer.params },
      startYearMonth: localActiveVer.startYearMonth,
    };

    try {
      const cal      = buildCalendar(newGlobalVersion.startYearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...newGlobalVersion.params }, newGlobalVersion.startYearMonth);

      runInAction(() => {
        this.dashboardRecords = this.dashboardRecords.map((d) => {
          if (d.id === GLOBAL_DASHBOARD_ID) {
            return { ...d, versions: [...d.versions, newGlobalVersion], activeVersionId: newVersionId, updatedAt: today };
          }
          if (d.id === localId) {
            return { ...d, isPublished: true, updatedAt: today };
          }
          return d;
        });
        this.activeDashboardId        = GLOBAL_DASHBOARD_ID;
        this.activeDashboardVersionId = newVersionId;
        this.activeView               = 'dashboard';
        this.startYearMonth           = newGlobalVersion.startYearMonth;
        this.planningParams           = { ...newGlobalVersion.params };
        this.dashboardData            = data;
        this.isJobRunning             = false;
        this.jobProgress              = 100;
        this.jobStatusText            = 'Complete';
        this.isDashboardLoading       = false;
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to promote dashboard.';
      runInAction(() => {
        this.dashboardError = message;
        this.isJobRunning   = false;
        this.isDashboardLoading = false;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // setGlobalActiveVersion — marks a different version as the active global one
  // (without triggering a full reload — just a fast switch like switchVersion)
  // ---------------------------------------------------------------------------
  async setGlobalActiveVersion(versionId: string): Promise<void> {
    const globalRec = this.dashboardRecords.find((d) => d.id === GLOBAL_DASHBOARD_ID);
    if (!globalRec) return;
    const ver = globalRec.versions.find((v) => v.versionId === versionId);
    if (!ver) return;

    const today = new Date().toISOString().slice(0, 10);
    runInAction(() => {
      this.dashboardRecords = this.dashboardRecords.map((d) => {
        if (d.id !== GLOBAL_DASHBOARD_ID) return d;
        return { ...d, activeVersionId: versionId, updatedAt: today };
      });
      // Also switch the viewing context to this version
      this.activeDashboardId        = GLOBAL_DASHBOARD_ID;
      this.activeDashboardVersionId = versionId;
      this.startYearMonth           = ver.startYearMonth;
      this.planningParams           = { ...ver.params };
      this.activeView               = 'dashboard';
      this.isDashboardLoading       = true;
    });

    try {
      await delay(600);
      const cal      = buildCalendar(ver.startYearMonth);
      const forecast = generateForecast(cal);
      const data     = computeDashboard(forecast, { ...ver.params }, ver.startYearMonth);
      runInAction(() => { this.dashboardData = data; this.isDashboardLoading = false; });
    } catch (err: unknown) {
      runInAction(() => { this.isDashboardLoading = false; });
    }
  }

  // ---------------------------------------------------------------------------
  // Convenience setters (called from UI without triggering a fetch)
  // ---------------------------------------------------------------------------
  setActiveView(view: 'listing' | 'dashboard' | 'explorer' | 'reference' | 'calculation'): void {
    runInAction(() => {
      this.activeView = view;
    });
  }

  // ---------------------------------------------------------------------------
  // setViewFilter — updates the UI-only view range filter (no recalculation)
  // ---------------------------------------------------------------------------
  setViewFilter(start: string, end: string): void {
    this.viewFilterStart = start;
    this.viewFilterEnd   = end;
  }

  // ---------------------------------------------------------------------------
  // filteredMonths — computed: subset of dashboardData.months within the filter
  // ---------------------------------------------------------------------------
  get filteredMonths(): string[] {
    if (!this.dashboardData) return [];
    const { months } = this.dashboardData;
    const { viewFilterStart: s, viewFilterEnd: e } = this;
    if (!s && !e) return months;
    return months.filter(m => (!s || m >= s) && (!e || m <= e));
  }
}
